import React, { useEffect, useRef, useState } from "react";
import { BoardAiApi, BoardAiAutomationResult } from "../../../services/board-ai-api";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton, {
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import "./new-board-modal.scss";
import AiSparkleIcon from "./ai-sparkle-icon";
import {
  AutomationAction,
  AutomationTrigger,
  BoardStatus,
  IssuePriority,
  PRIORITY_LABELS,
  PRIORITY_ORDER
} from "./types";
import { useBoardConfig } from "./use-board";
import { useBoardAiEnabled } from "./use-board-ai";

interface AutomationModalProps {
  open: boolean;
  onClose: () => void;
  // Rules are project-scoped — created for this project, and offered only this project's statuses.
  projectKey: string;
  // The parent owns persistence (so it can patch its config cache optimistically).
  onCreate: (name: string, trigger: AutomationTrigger, action: AutomationAction, notifyOnRun: boolean) => void;
}

type TriggerKind = AutomationTrigger["kind"];
type ActionKind = AutomationAction["kind"];

/** A rule is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { success: "Created" };

interface Option {
  value: string;
  label: string;
}

const TRIGGER_OPTIONS: Option[] = [
  { value: "status_changed_to", label: "Status changes to" },
  { value: "priority_changed_to", label: "Priority changes to" },
  { value: "issue_created", label: "Issue is created" },
  { value: "assignee_empty", label: "Unassigned when status becomes" }
];

const ACTION_OPTIONS: Option[] = [
  { value: "assign_to_me", label: "Assign to me" },
  { value: "set_priority", label: "Set priority" },
  { value: "move_to_status", label: "Move to status" },
  { value: "add_label", label: "Add label" },
  { value: "add_comment", label: "Add comment" }
];

const AutomationModal: React.FC<AutomationModalProps> = ({ open, onClose, projectKey, onCreate }) => {
  const { config } = useBoardConfig();
  const aiEnabled: boolean = useBoardAiEnabled();
  const projectStatuses: BoardStatus[] = config.statuses.filter(
    (status: BoardStatus) => status.projectKey === projectKey
  );
  const firstStatusId: string = projectStatuses[0]?.id ?? "";

  const [name, setName] = useState<string>("");
  const [triggerKind, setTriggerKind] = useState<TriggerKind>("status_changed_to");
  const [triggerStatusId, setTriggerStatusId] = useState<string>(firstStatusId);
  const [triggerPriority, setTriggerPriority] = useState<IssuePriority>("high");
  const [actionKind, setActionKind] = useState<ActionKind>("assign_to_me");
  const [label, setLabel] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [actionPriority, setActionPriority] = useState<IssuePriority>("high");
  const [actionStatusId, setActionStatusId] = useState<string>(firstStatusId);
  const [notifyOnRun, setNotifyOnRun] = useState<boolean>(true);
  // The rule joins the project's list at once (the caller stores it), so the commit has no loading
  // phase — only the ✓, held long enough to be read before the dialog leaves.
  const [createStatus, setCreateStatus] = useState<KuikaButtonStatus>("idle");
  const createMachineRef = useRef<SaveStatus | null>(null);
  if (createMachineRef.current === null) {
    createMachineRef.current = new SaveStatus(setCreateStatus);
  }
  const createMachine: SaveStatus = createMachineRef.current;
  useEffect((): (() => void) => (): void => createMachine.dispose(), [createMachine]);

  // "Describe in plain language" → generated rule that pre-fills the form below.
  const [aiText, setAiText] = useState<string>("");
  const [aiBusy, setAiBusy] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setTriggerKind("status_changed_to");
    setTriggerStatusId(firstStatusId);
    setTriggerPriority("high");
    setActionKind("assign_to_me");
    setLabel("");
    setBody("");
    setActionPriority("high");
    setActionStatusId(firstStatusId);
    setNotifyOnRun(true);
    setAiText("");
    setAiBusy(false);
    setAiError(null);
  }, [open, firstStatusId]);

  // Map a model-generated rule onto the typed form state. The backend has already validated the
  // kinds and referenced status/priority, so the casts are safe; an unusable rule comes back as an
  // error (Trigger/Action null) and is surfaced instead of applied.
  const applyGeneratedRule = (result: BoardAiAutomationResult): void => {
    if (result.error || !result.trigger || !result.action) {
      setAiError(result.error ?? "That couldn't be expressed as a rule.");
      return;
    }
    const trigger = result.trigger;
    const tKind = trigger.kind as TriggerKind;
    setTriggerKind(tKind);
    if (tKind === "priority_changed_to") {
      setTriggerPriority((trigger.priority as IssuePriority) ?? "high");
    } else {
      setTriggerStatusId(trigger.statusId ?? "");
    }

    const action = result.action;
    const aKind = action.kind as ActionKind;
    setActionKind(aKind);
    if (aKind === "add_label") {
      setLabel(action.label ?? "");
    } else if (aKind === "add_comment") {
      setBody(action.body ?? "");
    } else if (aKind === "set_priority") {
      setActionPriority((action.priority as IssuePriority) ?? "high");
    } else if (aKind === "move_to_status") {
      setActionStatusId(action.statusId ?? "");
    }

    if (result.name) {
      setName(result.name);
    }
    setAiError(null);
  };

  const handleGenerate = (): void => {
    const instruction: string = aiText.trim();
    if (!instruction || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    void BoardAiApi.toAutomationRule(instruction)
      .then(applyGeneratedRule)
      .catch((): void => setAiError("AI request failed. Please try again."))
      .finally((): void => setAiBusy(false));
  };

  const statusOptions: Option[] = projectStatuses.map((status: BoardStatus) => ({
    value: status.id,
    label: status.name
  }));
  const priorityOptions: Option[] = PRIORITY_ORDER.map((priority: IssuePriority) => ({
    value: priority,
    label: PRIORITY_LABELS[priority]
  }));

  const statusName = (id: string): string => projectStatuses.find((s: BoardStatus) => s.id === id)?.name ?? id;

  // Whether the trigger/action selections are complete enough to save.
  const triggerValid: boolean =
    triggerKind === "issue_created"
      ? true
      : triggerKind === "priority_changed_to"
        ? Boolean(triggerPriority)
        : Boolean(triggerStatusId);
  const actionValid: boolean =
    actionKind === "add_label"
      ? Boolean(label.trim())
      : actionKind === "add_comment"
        ? Boolean(body.trim())
        : actionKind === "set_priority"
          ? Boolean(actionPriority)
          : actionKind === "move_to_status"
            ? Boolean(actionStatusId)
            : true;

  const buildTrigger = (): AutomationTrigger => {
    switch (triggerKind) {
      case "priority_changed_to":
        return { kind: "priority_changed_to", priority: triggerPriority };
      case "issue_created":
        return { kind: "issue_created", statusId: triggerStatusId || undefined };
      case "assignee_empty":
        return { kind: "assignee_empty", statusId: triggerStatusId };
      default:
        return { kind: "status_changed_to", statusId: triggerStatusId };
    }
  };

  const buildAction = (): AutomationAction => {
    switch (actionKind) {
      case "add_label":
        return { kind: "add_label", label: label.trim() };
      case "add_comment":
        return { kind: "add_comment", body: body.trim() };
      case "set_priority":
        return { kind: "set_priority", priority: actionPriority };
      case "move_to_status":
        return { kind: "move_to_status", statusId: actionStatusId };
      default:
        return { kind: "assign_to_me" };
    }
  };

  const autoName = (): string => {
    const when: string =
      triggerKind === "priority_changed_to"
        ? `priority → ${PRIORITY_LABELS[triggerPriority]}`
        : triggerKind === "issue_created"
          ? triggerStatusId
            ? `created in ${statusName(triggerStatusId)}`
            : "created"
          : triggerKind === "assignee_empty"
            ? `unassigned in ${statusName(triggerStatusId)}`
            : `→ ${statusName(triggerStatusId)}`;
    const then: string =
      actionKind === "assign_to_me"
        ? "assign to me"
        : actionKind === "set_priority"
          ? `set ${PRIORITY_LABELS[actionPriority]}`
          : actionKind === "move_to_status"
            ? `move to ${statusName(actionStatusId)}`
            : actionKind === "add_label"
              ? `label ${label.trim()}`
              : "comment";
    return `When ${when}: ${then}`;
  };

  const handleCreate = (): void => {
    if (!triggerValid || !actionValid) return;
    // A second press during the ✓ would create the same rule twice.
    if (!createMachine.canStart) return;
    onCreate(name.trim() || autoName(), buildTrigger(), buildAction(), notifyOnRun);
    createMachine.succeed(onClose);
  };

  // Cancel, the X and Escape. During the ✓ hold the dialog is already on its way out; a close then
  // would let the pending one shut a dialog reopened in the meantime.
  const handleClose = (): void => {
    if (createMachine.current === "success") return;
    onClose();
  };

  return (
    <KModal
      showModal={open}
      closeModal={handleClose}
      width={460}
      header={<KuikaH5>New automation</KuikaH5>}
      footer={
        <KModalFooter
          onCancel={handleClose}
          submitLabel="Create rule"
          onSubmit={handleCreate}
          disabled={!triggerValid || !actionValid}
          status={createStatus}
          statusLabels={CREATE_STATUS_LABELS}
        />
      }
    >
      <div className="kuika-new-board-modal">
        {aiEnabled && (
          <div className="kuika-new-board-modal__field">
            <KuikaTextMedium>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <AiSparkleIcon size={13} />
                Describe in plain language
              </span>
            </KuikaTextMedium>
            <div style={{ display: "flex", gap: 8 }}>
              <KuikaInput
                value={aiText}
                onChange={(_event, value: string | number) => setAiText(String(value))}
                placeholder="e.g. when an issue moves to Done, comment 'Shipped!'"
              />
              <KuikaButton type="secondary" onClick={handleGenerate} disabled={!aiText.trim() || aiBusy}>
                {aiBusy ? "Generating…" : "Generate"}
              </KuikaButton>
            </div>
            {aiError && <span style={{ color: "var(--red-6)", fontSize: 12 }}>{aiError}</span>}
          </div>
        )}

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>When…</KuikaTextMedium>
          <KuikaSelectbox
            style={{ width: "100%" }}
            value={triggerKind}
            onChange={(value: string | number | null) => setTriggerKind((value as TriggerKind) ?? "status_changed_to")}
            options={TRIGGER_OPTIONS}
          />
          {triggerKind === "priority_changed_to" ? (
            <KuikaSelectbox
              style={{ width: "100%" }}
              value={triggerPriority}
              onChange={(value: string | number | null) => setTriggerPriority((value as IssuePriority) ?? "high")}
              options={priorityOptions}
            />
          ) : (
            <KuikaSelectbox
              style={{ width: "100%" }}
              allowClear={triggerKind === "issue_created"}
              placeholder={triggerKind === "issue_created" ? "Any status" : "Select a status"}
              value={triggerStatusId || undefined}
              onChange={(value: string | number | null) => setTriggerStatusId(value ? String(value) : "")}
              options={statusOptions}
            />
          )}
        </div>

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Then…</KuikaTextMedium>
          <KuikaSelectbox
            style={{ width: "100%" }}
            value={actionKind}
            onChange={(value: string | number | null) => setActionKind((value as ActionKind) ?? "assign_to_me")}
            options={ACTION_OPTIONS}
          />
          {actionKind === "set_priority" && (
            <KuikaSelectbox
              style={{ width: "100%" }}
              value={actionPriority}
              onChange={(value: string | number | null) => setActionPriority((value as IssuePriority) ?? "high")}
              options={priorityOptions}
            />
          )}
          {actionKind === "move_to_status" && (
            <KuikaSelectbox
              style={{ width: "100%" }}
              placeholder="Select a status"
              value={actionStatusId || undefined}
              onChange={(value: string | number | null) => setActionStatusId(value ? String(value) : "")}
              options={statusOptions}
            />
          )}
          {actionKind === "add_label" && (
            <KuikaInput
              value={label}
              onChange={(_event, value: string | number) => setLabel(String(value))}
              placeholder="Label name"
            />
          )}
          {actionKind === "add_comment" && (
            <KuikaInput
              value={body}
              onChange={(_event, value: string | number) => setBody(String(value))}
              placeholder="Comment body"
            />
          )}
        </div>

        <div className="kuika-new-board-modal__field">
          <KuikaTextMedium>Name (optional)</KuikaTextMedium>
          <KuikaInput
            value={name}
            onChange={(_event, value: string | number) => setName(String(value))}
            placeholder="Auto-generated from the rule"
          />
        </div>

        <div className="kuika-new-board-modal__field">
          <KuikaCheckbox
            label="Email the assignee and watchers when this runs"
            checked={notifyOnRun}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setNotifyOnRun(event.target.checked)}
          />
        </div>
      </div>
    </KModal>
  );
};

export default AutomationModal;
